import { useState, useEffect, useCallback } from 'react';
import { Building } from '../types/building';
import { supabase } from '../lib/supabase';
import { 
  getBuildings, 
  getArchivedBuildings, 
  deleteBuilding, 
  archiveBuilding, 
  unarchiveBuilding,
  getBuildingCompletionPercentage,
  createBuilding,
  updateBuilding
} from '../lib/localDatabase';
import { isOfflineModeEnabled } from '../lib/offlineStorage';
import { addAuditLog } from '../lib/auditLog';

export const useLocalBuildings = (showArchived = false) => {
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadBuildings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // If offline mode is enabled, use local storage
      if (isOfflineModeEnabled()) {
        const loadedBuildings = showArchived ? getArchivedBuildings() : getBuildings();
        setBuildings(loadedBuildings);
        return;
      }
      
      // Otherwise, fetch from Supabase
      const query = supabase
        .from('buildings')
        .select(`
          id,
          name,
          address,
          organization_id,
          created_at,
          updated_at,
          created_by,
          archived_at,
          metadata,
          floors (
            id,
            number,
            custom_fields,
            apartments (
              id,
              number,
              formatted_number,
              status,
              notes,
              area_type,
              abort_reason,
              technician,
              konzern_id,
              last_updated,
              custom_fields,
              history
            )
          )
        `)
        .order('name');
        
      // Filter by archived status
      if (showArchived) {
        query.not('archived_at', 'is', null);
      } else {
        query.is('archived_at', null);
      }
      
      const { data, error: fetchError } = await query;
      
      if (fetchError) {
        console.error('Error fetching buildings:', fetchError);
        setError('Fehler beim Laden der Gebäude');
        
        // Fallback to local storage
        const loadedBuildings = showArchived ? getArchivedBuildings() : getBuildings();
        setBuildings(loadedBuildings);
        return;
      }
      
      // Transform data to match our Building type
      const transformedBuildings: Building[] = data.map(building => ({
        id: building.id,
        organizationId: building.organization_id,
        name: building.name,
        address: building.address,
        floors: (building.floors || []).map(floor => ({
          id: floor.id,
          number: floor.number,
          apartments: (floor.apartments || []).map(apt => ({
            id: apt.id,
            number: apt.number,
            formattedNumber: apt.formatted_number,
            status: apt.status as any,
            notes: apt.notes || '',
            areaType: apt.area_type as any,
            abortReason: apt.abort_reason,
            technician: apt.technician,
            konzernId: apt.konzern_id,
            lastUpdated: apt.last_updated ? new Date(apt.last_updated) : undefined,
            customFields: apt.custom_fields,
            history: apt.history
          })),
          customFields: floor.custom_fields
        })),
        archivedAt: building.archived_at ? new Date(building.archived_at) : undefined,
        customFields: building.metadata?.customFields,
        metadata: building.metadata
      }));
      
      setBuildings(transformedBuildings);
    } catch (err) {
      console.error('Error loading buildings:', err);
      setError('Fehler beim Laden der Gebäude');
      
      // Fallback to local storage
      const loadedBuildings = showArchived ? getArchivedBuildings() : getBuildings();
      setBuildings(loadedBuildings);
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  useEffect(() => {
    loadBuildings();
  }, [loadBuildings, showArchived]);

  const removeBuilding = useCallback(async (id: string) => {
    try {
      // If offline mode is enabled, use local storage
      if (isOfflineModeEnabled()) {
        deleteBuilding(id, showArchived);
        setBuildings(prev => prev.filter(b => b.id !== id));
        
        // Log the action
        addAuditLog('delete', 'building', `Building deleted: ${id}`, id);
        return true;
      }
      
      // Otherwise, delete from Supabase
      const { error: deleteError } = await supabase
        .from('buildings')
        .delete()
        .eq('id', id);
        
      if (deleteError) {
        console.error('Error deleting building:', deleteError);
        throw deleteError;
      }
      
      setBuildings(prev => prev.filter(b => b.id !== id));
      
      // Log the action
      addAuditLog('delete', 'building', `Building deleted: ${id}`, id);
      return true;
    } catch (err) {
      console.error('Error deleting building:', err);
      setError('Fehler beim Löschen des Gebäudes');
      return false;
    }
  }, [showArchived]);

  const moveToArchive = useCallback(async (id: string) => {
    try {
      // If offline mode is enabled, use local storage
      if (isOfflineModeEnabled()) {
        const archived = archiveBuilding(id);
        if (archived) {
          setBuildings(prev => prev.filter(b => b.id !== id));
          
          // Log the action
          addAuditLog('update', 'building', `Building archived: ${id}`, id);
          return true;
        }
        return false;
      }
      
      // Otherwise, update in Supabase
      const { error: updateError } = await supabase
        .from('buildings')
        .update({
          archived_at: new Date().toISOString(),
          metadata: {
            status: 'archived',
            actualCompletionDate: new Date().toISOString()
          }
        })
        .eq('id', id);
        
      if (updateError) {
        console.error('Error archiving building:', updateError);
        throw updateError;
      }
      
      setBuildings(prev => prev.filter(b => b.id !== id));
      
      // Log the action
      addAuditLog('update', 'building', `Building archived: ${id}`, id);
      return true;
    } catch (err) {
      console.error('Error archiving building:', err);
      setError('Fehler beim Archivieren des Gebäudes');
      return false;
    }
  }, []);

  const restoreFromArchive = useCallback(async (id: string) => {
    try {
      // If offline mode is enabled, use local storage
      if (isOfflineModeEnabled()) {
        const unarchived = unarchiveBuilding(id);
        if (unarchived) {
          setBuildings(prev => prev.filter(b => b.id !== id));
          
          // Log the action
          addAuditLog('update', 'building', `Building unarchived: ${id}`, id);
          return true;
        }
        return false;
      }
      
      // Otherwise, update in Supabase
      const { error: updateError } = await supabase
        .from('buildings')
        .update({
          archived_at: null,
          metadata: {
            status: 'completed'
          }
        })
        .eq('id', id);
        
      if (updateError) {
        console.error('Error unarchiving building:', updateError);
        throw updateError;
      }
      
      setBuildings(prev => prev.filter(b => b.id !== id));
      
      // Log the action
      addAuditLog('update', 'building', `Building unarchived: ${id}`, id);
      return true;
    } catch (err) {
      console.error('Error unarchiving building:', err);
      setError('Fehler beim Wiederherstellen des Gebäudes');
      return false;
    }
  }, []);

  const importBuilding = useCallback(async (building: Building) => {
    try {
      // Check if a building with the same name already exists
      const existingBuildings = getBuildings();
      const existingBuildingWithSameName = existingBuildings.find(b => 
        b.name.toLowerCase() === building.name.toLowerCase()
      );
      
      if (existingBuildingWithSameName) {
        // Add a suffix to make the name unique
        building.name = `${building.name} (Importiert ${new Date().toLocaleDateString()})`;
      }
      
      // If offline mode is enabled, use local storage
      if (isOfflineModeEnabled()) {
        // Create the building
        const newBuilding = createBuilding(building.name, building.address);
        
        // Update with floors and apartments
        const updatedBuilding = updateBuilding(newBuilding.id, {
          ...newBuilding,
          floors: building.floors
        });
        
        // Log the action
        addAuditLog('create', 'building', `Building imported: ${building.name}`, newBuilding.id);
        
        return updatedBuilding;
      }
      
      // Otherwise, create in Supabase
      const { data: buildingData, error: buildingError } = await supabase
        .from('buildings')
        .insert({
          name: building.name,
          address: building.address,
          organization_id: building.organizationId || 'default-org',
          metadata: building.metadata
        })
        .select()
        .single();
        
      if (buildingError) {
        console.error('Error creating building:', buildingError);
        throw buildingError;
      }
      
      // Create floors and apartments
      for (const floor of building.floors) {
        const { data: floorData, error: floorError } = await supabase
          .from('floors')
          .insert({
            building_id: buildingData.id,
            number: floor.number,
            custom_fields: floor.customFields
          })
          .select()
          .single();
          
        if (floorError) {
          console.error('Error creating floor:', floorError);
          continue;
        }
        
        // Create apartments
        for (const apt of floor.apartments) {
          const { error: apartmentError } = await supabase
            .from('apartments')
            .insert({
              floor_id: floorData.id,
              number: apt.number,
              formatted_number: apt.formattedNumber,
              status: apt.status,
              notes: apt.notes,
              area_type: apt.areaType,
              abort_reason: apt.abortReason,
              technician: apt.technician,
              konzern_id: apt.konzernId,
              last_updated: apt.lastUpdated?.toISOString(),
              custom_fields: apt.customFields,
              history: apt.history
            });
            
          if (apartmentError) {
            console.error('Error creating apartment:', apartmentError);
          }
        }
      }
      
      // Log the action
      addAuditLog('create', 'building', `Building imported: ${building.name}`, buildingData.id);
      
      // Reload buildings
      await loadBuildings();
      
      return buildingData;
    } catch (err) {
      console.error('Error importing building:', err);
      setError('Fehler beim Importieren des Gebäudes');
      return null;
    }
  }, [loadBuildings]);

  const refreshBuildings = useCallback(async () => {
    return loadBuildings();
  }, [loadBuildings]);

  const getBuildingCompletion = useCallback((building: Building): number => {
    return getBuildingCompletionPercentage(building);
  }, []);

  return {
    buildings,
    loading,
    error,
    removeBuilding,
    moveToArchive,
    restoreFromArchive,
    importBuilding,
    refreshBuildings,
    getBuildingCompletion
  };
};